function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useState } from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import clsx from 'clsx';
import { ModalReact, ModalType } from 'common/components/modal';
import { Theme } from 'common/report/queries/app-api/themes';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { Visibility } from 'common/components/base/stackable';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { InputReact } from 'common/components/input';
import { useThemes } from 'common/contexts/react/themes-context';
import { useTranslation } from 'common/i18n/hooks';
import { ProjectShareLevel } from 'common/report/constants';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { ColorThemeSwatch } from '../swatch';
import styles from './index.module.styl';
export type SaveThemeModalResult = {
  updatedTheme: Theme;
};
function SaveThemeModal(props: {
  theme: Theme;
} & InstanceProps<SaveThemeModalResult>) {
  const {
    t
  } = useTranslation();
  const themesResource = useThemes();
  const shareLevelLabels = {
    [ProjectShareLevel.Off]: t("common.off"),
    [ProjectShareLevel.Viewer]: t("mixology.viewer"),
    [ProjectShareLevel.Editor]: t("mixology.editor")
  } as const;
  const [isSaving, setIsSaving] = useState(false);
  const [themeName, setThemeName] = useState(props.theme.id ? props.theme.name : "");
  const [projectShareSetting, setProjectShareSetting] = useState<`${ProjectShareLevel}`>(props.theme.global_access_type || ProjectShareLevel.Off);
  const onProjectShareChange = _ref => {
    let {
      detail
    } = _ref;
    setProjectShareSetting(detail.item.value);
  };
  const save = () => {
    if (props.theme.id && !Number.isInteger(props.theme.id)) {
      console.error("Cannot save a static theme");
      return;
    }
    setIsSaving(true);
    if (!props.theme.id) {
      // create theme. the endpoint includes global_access_type option for setting initial project sharing
      themesResource.create(_objectSpread(_objectSpread({}, props.theme), {}, {
        name: themeName,
        global_access_type: projectShareSetting
      })).then(result => {
        const newTheme = Object.values(result)[0];
        props.onResolve({
          updatedTheme: newTheme
        });
        addToast({
          message: t("chart-customization.theme-saved"),
          type: ToastType.SUCCESS
        });
      }).catch(() => {
        addToast({
          message: t("chart-customization.theme-save-error"),
          type: ToastType.ERROR
        });
      }).finally(() => {
        setIsSaving(false);
      });
    } else {
      themesResource.patch(props.theme.id as number, _objectSpread(_objectSpread({}, props.theme), {}, {
        name: themeName,
        global_access_type: projectShareSetting
      })).then(result => {
        const updatedTheme = Object.values(result)[0];
        props.onResolve({
          updatedTheme
        });
        addToast({
          message: t("chart-customization.theme-updated"),
          type: ToastType.SUCCESS
        });
      }).catch(() => {
        addToast({
          message: t("chart-customization.theme-update-error"),
          type: ToastType.ERROR
        });
      }).finally(() => {
        setIsSaving(false);
      });
    }
  };
  const nameError = themesResource.getAllValues().filter(theme => theme.id !== props.theme.id).some(theme => theme.name === themeName.trim().toLowerCase()) ? t("chart-customization.duplicate-name-error") : null;
  const formValid = Boolean(themeName) && !nameError;
  const handleKeydown = ev => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      if (formValid) {
        save();
      }
    }
  };
  return <ModalReact modalType={ModalType.Modal} open={props.isOpen} modalTitle={t("chart-customization.save-theme")} modalDescription={t("chart-customization.save-theme-modal-description")} growToContent closeable onChange={_ref2 => {
    let {
      detail
    } = _ref2;
    if (detail.state === Visibility.Closed) {
      props.onReject();
    }
  }} data-sentry-element="ModalReact" data-sentry-component="SaveThemeModal" data-sentry-source-file="index.tsx">
      <div className={styles.content}>
        <div className={styles.header}>{t("chart-customization.save-theme")}</div>
        <InputReact className={styles.nameInput} label={t("chart-customization.theme-name")} placeholder={t("chart-customization.new-theme")} value={themeName} onInput={ev => {
        setThemeName(ev.target.value || "");
      }} validationStatus={nameError ? "invalid" : ""} errorMessage={nameError || ""} onKeydown={handleKeydown} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
        <div className={styles.colorSwatch}>
          {t("common.light-mode")}
          <ColorThemeSwatch theme={props.theme} hideName data-sentry-element="ColorThemeSwatch" data-sentry-source-file="index.tsx" />
        </div>
        <div className={clsx(styles.colorSwatch, styles.darkMode)}>
          {t("common.dark-mode")}
          <ColorThemeSwatch theme={props.theme} hideName darkMode data-sentry-element="ColorThemeSwatch" data-sentry-source-file="index.tsx" />
        </div>
        <div className={styles.shareRow}>
          <div className={styles.shareLabel}>{t("chart-customization.project-share-label")}</div>
          <SelectReact alwaysShowCaret disableSearch menuWidth={120} theme={SelectTheme.Tertiary} selectedOption={shareLevelLabels[projectShareSetting]} sections={[{
          items: Object.entries(shareLevelLabels).map(_ref3 => {
            let [value, label] = _ref3;
            return {
              label,
              value
            };
          })
        }]} onChange={onProjectShareChange} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
        </div>
        <footer className={styles.footer}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={() => {
          props.onReject();
        }} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {t("common.cancel")}
          </ButtonReact>
          <ButtonReact theme={ButtonTheme.Primary} onClick={() => save()} disabled={!formValid || isSaving} icon={isSaving ? "spinner" : ""} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {t("common.save")}
          </ButtonReact>
        </footer>
      </div>
    </ModalReact>;
}
export const launchSaveThemeModal = create(SaveThemeModal);